// ---------------------------------------------------------------------------
// overpassPlaces — the FREE twin of googlePlaces.searchPlacesAlongRoute.
//
// POST_LAUNCH_TODO item 0: Google SearchNearby was the dominant API cost
// (~$30/1000 calls; the June leak hit 70k calls). The information it buys —
// "are there notable parks / attractions / cafés along this route" — lives
// in OpenStreetMap, which we can now query for $0 on the self-hosted
// Overpass instance (VITE_OVERPASS_ENDPOINT, public mirrors as fallback,
// native CORS handled — all via overpassDirect).
//
// Returns the SAME GooglePlaceHit shape so scoring/segments need no changes.
// Two honest differences from Google:
//   • OSM has no ratings. Google hits are pre-filtered to rating ≥ 4.0 with
//     ≥ 20 reviews; our notability proxy is "named + specifically tagged in
//     OSM," and we assign sentinel rating values that pass downstream
//     thresholds. A named park in OSM is comparable evidence of "nice thing
//     here" to a 4-star Google park.
//   • Curated blurbs are absent (Google path doesn't have them either —
//     those come from the curated-roads corpus).
//
// generateRoutes uses this FIRST and falls back to the paid Google call
// only when this returns nothing (Overpass outage / uncovered region).
// ---------------------------------------------------------------------------

import { overpassDirect } from "@/api/osm";
import { PLACE_TYPE_WEIGHTS, type GooglePlaceHit, type GooglePlaceType } from "@/api/googlePlaces";
import { bbox, nearestDistance, samplePolylineMax, type LngLat } from "@/utils/polyline";

/** OSM tag selectors per internal place type. NAMED features only — a name
 *  is OSM's cheapest notability signal and matches how the Google path
 *  filters junk. Mirrors TYPE_TO_GOOGLE in googlePlaces.ts. */
const TYPE_SELECTORS: Array<[GooglePlaceType, string[]]> = [
  ["park", ['leisure~"^(park|garden)$"']],
  ["tourist_attraction", ['tourism~"^(attraction|viewpoint|artwork)$"']],
  ["museum", ['tourism~"^(museum|gallery)$"']],
  ["church", ['amenity="place_of_worship"']],
  ["library", ['amenity="library"']],
  ["cafe", ['amenity="cafe"', 'shop="bakery"']],
  ["shop", ['shop~"^(books|gift|art|jewelry|antiques|florist)$"']],
  ["restaurant", ['amenity="restaurant"']],
];

/** Sentinels that satisfy downstream "highly-rated" gates (Google hits are
 *  pre-filtered to rating ≥ 4.0, ≥ 20 reviews before they reach scoring). */
const OSM_SENTINEL_RATING = 4.2;
const OSM_SENTINEL_REVIEWS = 25;

export async function searchPlacesAlongRouteOverpass(
  coordinates: LngLat[],
  opts: { radiusM?: number; maxResults?: number; timeoutMs?: number } = {},
): Promise<GooglePlaceHit[]> {
  const radiusM = opts.radiusM ?? 250;
  const maxResults = opts.maxResults ?? 60;
  if (!coordinates || coordinates.length < 2) return [];

  const b = bbox(coordinates, 0.004); // ~400 m pad around the route
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;
  const clauses = TYPE_SELECTORS.flatMap(([, sels]) =>
    sels.flatMap((sel) => [
      `node[${sel}]["name"](${bb});`,
      `way[${sel}]["name"](${bb});`,
    ]),
  ).join("\n  ");
  const query = `[out:json][timeout:20];\n(\n  ${clauses}\n);\nout tags center 300;`;

  const elements = await overpassDirect(query, opts.timeoutMs ?? 8_000);
  if (!elements.length) return [];

  // Distance-filter against a downsampled polyline (bbox alone would count
  // places far off the line on L-shaped routes).
  const line = samplePolylineMax(coordinates, 300);

  const classify = (tags: Record<string, string>): GooglePlaceType | null => {
    const leisure = tags.leisure ?? "", tourism = tags.tourism ?? "",
      amenity = tags.amenity ?? "", shop = tags.shop ?? "";
    if (/^(park|garden)$/.test(leisure)) return "park";
    if (/^(attraction|viewpoint|artwork)$/.test(tourism)) return "tourist_attraction";
    if (/^(museum|gallery)$/.test(tourism)) return "museum";
    if (amenity === "place_of_worship") return "church";
    if (amenity === "library") return "library";
    if (amenity === "cafe" || shop === "bakery") return "cafe";
    if (/^(books|gift|art|jewelry|antiques|florist)$/.test(shop)) return "shop";
    if (amenity === "restaurant") return "restaurant";
    return null;
  };

  const seen = new Set<string>();
  const hits: GooglePlaceHit[] = [];
  for (const el of elements) {
    const tags = el.tags ?? {};
    const name = tags.name;
    if (!name) continue;
    const type = classify(tags);
    if (!type || PLACE_TYPE_WEIGHTS[type] <= 0) continue;
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (typeof lat !== "number" || typeof lon !== "number") continue;
    const center: LngLat = [lon, lat];
    if (nearestDistance(center, line) > radiusM) continue;
    const key = `${type}:${name.toLowerCase()}`;
    if (seen.has(key)) continue; // dedupe same-named place (node + way twins)
    seen.add(key);
    hits.push({
      id: `osm-${el.type ?? "n"}-${el.id ?? hits.length}`,
      name,
      type,
      weight: PLACE_TYPE_WEIGHTS[type],
      rating: OSM_SENTINEL_RATING,
      userRatingCount: OSM_SENTINEL_REVIEWS,
      center,
    });
  }

  // Keep the most pleasant-relevant when over cap (parks & attractions first).
  hits.sort((a, b) => b.weight - a.weight);
  return hits.slice(0, maxResults);
}
